import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ChevronDown,
  AlertTriangle,
  Cpu,
  Images,
  HardDrive,
  Crosshair,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import type { MLExportRunPlan, PlanBucket, PlanSelectorDiagnostics } from "../../types/run";
import { DATASET_DESTINATION } from "../../types/run";
import type { TaxonomyFamily } from "../../types/taxonomy";
import PlanSelectorBanner from "./plan-selector-banner";

/* ──────────────────────────────────────────────────────────────────
 * PlanPreview — preflight dashboard for ML export.
 *
 * Compact, scannable, professional. Three sections:
 *   1. Metric cards — key numbers at a glance
 *   2. Class balance — compact table, warn only on zero
 *   3. Per-machine — collapsible buckets
 * ────────────────────────────────────────────────────────────────── */

interface PlanPreviewProps {
  plan: MLExportRunPlan;
  families?: TaxonomyFamily[];
  labelColors?: Record<string, string>;
  onRelaxFilters?: () => void;
  /**
   * Destination code the plan was computed for. A `dataset` run writes a
   * manifest and copies no images, so the byte/egress estimates below do not
   * describe it — see the Size card.
   */
  destination?: string | null;
}

/* ─── Helpers ──────────────────────────────────────────────────── */

const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
const twoDecimals = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

const formatBytes = (bytes: number, t: TFunction<"mlExports">): string => {
  if (bytes < 1024) return t("plan.bytes.b", { value: formatNumber(bytes) });
  if (bytes < 1024 * 1024)
    return t("plan.bytes.kib", { value: formatNumber(bytes / 1024, oneDecimal) });
  if (bytes < 1024 * 1024 * 1024)
    return t("plan.bytes.mib", {
      value: formatNumber(bytes / 1024 / 1024, oneDecimal),
    });
  return t("plan.bytes.gib", {
    value: formatNumber(bytes / 1024 / 1024 / 1024, twoDecimals),
  });
};

const pct = (v: number, t: TFunction<"mlExports">) =>
  t("plan.percent", { value: formatNumber(Math.round(v * 100)) });

const SPLIT_COLORS: Record<string, string> = {
  train: "bg-blue-500",
  val: "bg-amber-500",
  test: "bg-emerald-500",
};

/** Split names are wire values; only their display text is translated. */
const SPLIT_LABEL_KEYS: Record<
  string,
  "splits.train" | "splits.val" | "splits.test"
> = {
  train: "splits.train",
  val: "splits.val",
  test: "splits.test",
};

const splitLabel = (split: string, t: TFunction<"mlExports">): string => {
  const key = SPLIT_LABEL_KEYS[split];
  return key ? t(key) : split;
};

/* ─── Metric card ─────────────────────────────────────────────── */

const MetricCard = ({
  icon,
  label,
  value,
  sub,
  status = "ok",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  status?: "ok" | "warn" | "error";
}) => (
  <div className={cn(
    "rounded-md border px-3 py-2 flex items-start gap-2.5",
    status === "ok" && "bg-muted/20 border-border",
    status === "warn" && "bg-amber-500/5 border-amber-500/30",
    status === "error" && "bg-red-500/5 border-red-500/30",
  )}>
    <div className={cn(
      "mt-0.5",
      status === "ok" && "text-muted-foreground",
      status === "warn" && "text-amber-500",
      status === "error" && "text-red-500",
    )}>
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none">
        {label}
      </div>
      <div className={cn(
        "text-sm font-semibold tabular-nums leading-tight mt-0.5",
        status === "error" && "text-red-500",
        status === "warn" && "text-amber-600 dark:text-amber-400",
      )}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
          {sub}
        </div>
      )}
    </div>
  </div>
);

/* ─── Family distribution chart ────────────────────────────────── */

/**
 * Per-family member distribution. Generalizes the former two-sided
 * butterfly chart to N members: each family gets a proportional
 * stacked bar (one colored segment per member) plus per-member counts.
 * Members with zero count while siblings have data are flagged amber.
 */
const FamilyDistributionChart = ({
  labels,
  families,
  labelColors,
}: {
  labels: Record<string, number>;
  families: TaxonomyFamily[];
  labelColors?: Record<string, string>;
}) => {
  const { t } = useTranslation("mlExports");
  return (
    <div className="space-y-1">
      {families.map((family) => {
        const counts = family.members.map(
          (member) => labels[member.canonical] ?? 0,
        );
        const total = counts.reduce((sum, c) => sum + c, 0);
        const hasMissing = total > 0 && counts.some((c) => c === 0);

        return (
          <div key={family.key} className="flex items-center gap-2 h-7">
            {/* Family name */}
            <div className="w-[90px] shrink-0">
              <span className="text-[9px] text-muted-foreground leading-none truncate block">
                {family.display_name}
              </span>
            </div>

            {/* Stacked proportional bar — one segment per member */}
            <div className="flex-1 flex h-4 rounded-sm overflow-hidden bg-muted/40">
              {total > 0 &&
                family.members.map((member, i) =>
                  counts[i] > 0 ? (
                    <div
                      key={member.canonical}
                      className="h-full transition-all"
                      style={{
                        width: `${Math.max((counts[i] / total) * 100, 3)}%`,
                        backgroundColor:
                          labelColors?.[member.canonical] ?? "#888",
                        opacity: 0.85,
                      }}
                      title={t("plan.memberCount", {
                        name: member.display_name,
                        value: formatNumber(counts[i]),
                      })}
                    />
                  ) : null,
                )}
            </div>

            {/* Per-member counts */}
            <div className="shrink-0 flex items-center gap-1.5">
              {family.members.map((member, i) => (
                <span
                  key={member.canonical}
                  className={cn(
                    "inline-flex items-center gap-1 text-[10px] font-mono tabular-nums",
                    counts[i] === 0 && total > 0
                      ? "text-amber-500 font-semibold"
                      : "text-foreground",
                  )}
                  title={member.display_name}
                >
                  <span
                    className="h-2 w-2 rounded-sm shrink-0"
                    style={{
                      backgroundColor: labelColors?.[member.canonical] ?? "#888",
                    }}
                  />
                  {formatNumber(counts[i])}
                </span>
              ))}
            </div>

            {/* Missing class warning */}
            {hasMissing && (
              <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Split bar ───────────────────────────────────────────────── */

const SplitBar = ({ splits, total }: { splits: Record<string, number>; total: number }) => {
  const { t } = useTranslation("mlExports");
  const entries = Object.entries(splits);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="flex h-2 rounded-full overflow-hidden bg-muted/50">
        {entries.map(([split, count]) => (
          <div
            key={split}
            className={cn("h-full", SPLIT_COLORS[split] ?? "bg-zinc-400")}
            style={{ width: `${(count / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="flex items-center gap-3">
        {entries.map(([split, count]) => (
          <span key={split} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <span className={cn("h-1.5 w-1.5 rounded-sm", SPLIT_COLORS[split] ?? "bg-zinc-400")} />
            {splitLabel(split, t)} {formatNumber(count)}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ─── Per-machine bucket row ──────────────────────────────────── */

const BucketRow = ({ bucket, families, labelColors }: {
  bucket: PlanBucket;
  families?: TaxonomyFamily[];
  labelColors?: Record<string, string>;
}) => {
  const { t } = useTranslation("mlExports");
  const [expanded, setExpanded] = useState(false);
  const q = bucket.quality;
  const hasMistakes = q.mistakes_count > 0;
  const highMistakes = q.mistakes_share_of_bucket > 0.3;

  return (
    <div className={cn(
      "rounded-md border bg-background",
      highMistakes && "border-amber-500/30",
    )}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold truncate">{bucket.machine.name}</span>
            <span className="text-[10px] font-mono text-muted-foreground tabular-nums">
              {t("plan.bucket.summary", {
                value: formatNumber(bucket.candidates.count),
                share: pct(bucket.candidates.share_of_index, t),
              })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasMistakes && (
            <span className={cn(
              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold",
              highMistakes
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                : "bg-muted text-muted-foreground",
            )}>
              <AlertTriangle className="h-2.5 w-2.5" />
              {formatNumber(q.mistakes_count)}
            </span>
          )}
          <ChevronDown className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )} />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2.5 border-t border-border/50">
          {families && families.length > 0 && (
            <FamilyDistributionChart labels={bucket.labels} families={families} labelColors={labelColors} />
          )}
          <SplitBar splits={bucket.splits} total={bucket.candidates.count} />
          {hasMistakes && (
            <div className={cn(
              "flex items-center gap-2 rounded px-2 py-1.5 text-[11px]",
              highMistakes
                ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                : "bg-muted/50 text-muted-foreground",
            )}>
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {t("plan.bucket.mistakes", {
                value: formatNumber(q.mistakes_count),
                share: pct(q.mistakes_share_of_bucket, t),
              })}
              {highMistakes && t("plan.bucket.reviewBeforeExport")}
            </div>
          )}
          <div className="text-[10px] text-muted-foreground font-mono">
            {t("plan.bucket.span", {
              start: formatDate(new Date(bucket.span.start)),
              end: formatDate(new Date(bucket.span.end)),
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Selector funnel ──────────────────────────────────────────── */

const SelectorFunnel = ({ selector }: { selector: PlanSelectorDiagnostics }) => {
  const { t } = useTranslation("mlExports");
  const { input, data, filters, yielded } = selector;
  const dropped = input.requested - yielded;
  if (dropped === 0) return null;

  const reasons: Array<{ label: string; count: number }> = [];
  if (data.legacy_labels_only > 0) reasons.push({ label: t("plan.reason.legacyOnly"), count: data.legacy_labels_only });
  if (data.binary_pair_conflict > 0) reasons.push({ label: t("plan.reason.binaryConflict"), count: data.binary_pair_conflict });
  if (data.metadata_incomplete > 0) reasons.push({ label: t("plan.reason.missingMetadata"), count: data.metadata_incomplete });
  if (data.no_classifications > 0) reasons.push({ label: t("plan.reason.noClassifications"), count: data.no_classifications });
  if (filters.confidence_excluded > 0) reasons.push({ label: t("plan.reason.confidenceExcluded"), count: filters.confidence_excluded });
  if (filters.only_mistakes_excluded > 0) reasons.push({ label: t("plan.reason.onlyMistakes"), count: filters.only_mistakes_excluded });

  return (
    <div className="rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs space-y-1">
      <div className="font-medium text-amber-700 dark:text-amber-300">
        {t("plan.funnel.summary", {
          requested: formatNumber(input.requested),
          yielded: formatNumber(yielded),
        })}
      </div>
      {reasons.map((r) => (
        <div key={r.label} className="flex items-center gap-1.5 text-muted-foreground pl-2">
          <span className="text-amber-500">·</span>
          {formatNumber(r.count)} {r.label}
        </div>
      ))}
    </div>
  );
};

/* ─── Main component ───────────────────────────────────────────── */

const PlanPreview = ({ plan, families, labelColors, onRelaxFilters, destination }: PlanPreviewProps) => {
  const { t } = useTranslation("mlExports");
  const { overview, labels_overall, splits_overall, buckets, cost, limits, selector } = plan;
  const [showBalance, setShowBalance] = useState(true);

  const isEmpty = selector?.yielded === 0 || overview.total_candidates === 0;

  if (isEmpty) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-muted-foreground">{t("plan.nothingExported")}</div>
        {selector && <PlanSelectorBanner selector={selector} onRelaxFilters={onRelaxFilters} />}
      </div>
    );
  }

  const isMetadataOnly = destination === DATASET_DESTINATION;

  const mistakesTotal = buckets.reduce((s, b) => s + b.quality.mistakes_count, 0);
  const mistakesShare = overview.total_candidates > 0 ? mistakesTotal / overview.total_candidates : 0;

  const costLine = [
    cost.estimated_bytes != null
      ? t("plan.approxSize", { value: formatBytes(cost.estimated_bytes, t) })
      : null,
    cost.gcs_egress_cost_usd != null
      ? formatNumber(cost.gcs_egress_cost_usd, {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })
      : null,
  ].filter(Boolean).join(" · ");

  // Check if any family has a zero-count member while siblings have data
  const hasMissingClass = families?.some((f) => {
    const counts = f.members.map((m) => labels_overall[m.canonical] ?? 0);
    const total = counts.reduce((sum, c) => sum + c, 0);
    return total > 0 && counts.some((c) => c === 0);
  });

  return (
    <div className="space-y-3">
      {/* ─── Metric cards row ──────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          icon={<Images className="h-3.5 w-3.5" />}
          label={t("plan.metric.images")}
          value={formatNumber(overview.total_candidates)}
          sub={t("plan.metric.machines", {
            count: overview.total_machines,
            value: formatNumber(overview.total_machines),
          })}
        />
        {/* A dataset run writes a manifest and copies nothing, so bytes and
            egress cost are both zero and the wait is bounded by the write,
            not the image count. Showing "~4.2 GiB · $0.31" there would
            describe an upload that never happens. */}
        {isMetadataOnly ? (
          <MetricCard
            icon={<HardDrive className="h-3.5 w-3.5" />}
            label={t("plan.metric.size")}
            value={t("plan.metric.metadataValue")}
            sub={t("plan.metric.metadataSub")}
            status="ok"
          />
        ) : (
          <MetricCard
            icon={<HardDrive className="h-3.5 w-3.5" />}
            label={t("plan.metric.size")}
            value={costLine || "–"}
            sub={t("plan.metric.sizeSub", {
              used: formatNumber(limits.images_used),
              max:
                limits.images_max != null
                  ? formatNumber(limits.images_max)
                  : "–",
              mode:
                cost.mode === "approx"
                  ? t("plan.metric.modeEstimate")
                  : t("plan.metric.modeActual"),
            })}
            status={limits.over_any_limit && !limits.images_over_limit ? "error" : "ok"}
          />
        )}
        <MetricCard
          icon={<Crosshair className="h-3.5 w-3.5" />}
          label={t("plan.metric.quality")}
          value={
            mistakesTotal > 0
              ? t("plan.metric.mistakes", {
                  count: mistakesTotal,
                  value: formatNumber(mistakesTotal),
                })
              : t("plan.metric.clean")
          }
          sub={
            mistakesTotal > 0
              ? t("plan.metric.mistakesShare", { value: pct(mistakesShare, t) })
              : t("plan.metric.noCorrections")
          }
          status={mistakesTotal === 0 ? "ok" : mistakesShare > 0.3 ? "error" : "warn"}
        />
      </div>

      {/* ─── Splits ────────────────────────────────────────────── */}
      {Object.keys(splits_overall).length > 0 && (
        <SplitBar splits={splits_overall} total={overview.total_candidates} />
      )}

      {/* ─── Class balance ─────────────────────────────────────── */}
      {families && families.length > 0 && (
        <div className={cn(
          "rounded-md border overflow-hidden",
          hasMissingClass ? "border-amber-500/30" : "border-border",
        )}>
          <button
            type="button"
            onClick={() => setShowBalance(!showBalance)}
            className="w-full flex items-center justify-between px-3 py-1.5 bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("plan.classBalance")}
            </span>
            <div className="flex items-center gap-1.5">
              {hasMissingClass && (
                <span className="text-[9px] text-amber-500 font-medium">
                  {t("plan.missingClasses")}
                </span>
              )}
              <ChevronDown className={cn(
                "h-3 w-3 text-muted-foreground transition-transform",
                showBalance && "rotate-180",
              )} />
            </div>
          </button>
          {showBalance && (
            <div className="px-2 py-2">
              <FamilyDistributionChart labels={labels_overall} families={families} labelColors={labelColors} />
            </div>
          )}
        </div>
      )}

      {/* ─── Per-machine breakdown ─────────────────────────────── */}
      {buckets.length > 1 && (
        <details className="group">
          <summary className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors cursor-pointer list-none">
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
            <Cpu className="h-3 w-3" />
            {t("plan.machinesSummary", { value: formatNumber(buckets.length) })}
          </summary>
          <div className="mt-2 space-y-1.5">
            {buckets.map((b) => (
              <BucketRow key={b.machine.id} bucket={b} families={families} labelColors={labelColors} />
            ))}
          </div>
        </details>
      )}
      {buckets.length === 1 && (
        <BucketRow bucket={buckets[0]} families={families} labelColors={labelColors} />
      )}

      {/* ─── Selector funnel ───────────────────────────────────── */}
      {selector && selector.yielded < selector.input.requested && selector.yielded > 0 && (
        <SelectorFunnel selector={selector} />
      )}
      {selector && selector.yielded === 0 && (
        <PlanSelectorBanner selector={selector} onRelaxFilters={onRelaxFilters} />
      )}

      {/* ─── Limit warning ─────────────────────────────────────── */}
      {/* An over-the-cap image count is no longer a blocker: the panel splits the
          selection into chunked runs and says so above this preview. Only the
          non-image limits still warrant a warning here. */}
      {limits.over_any_limit && !limits.images_over_limit && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("plan.overLimit")}
        </div>
      )}
    </div>
  );
};

export default PlanPreview;
